import { SiloBag } from '../../commercial/models/silo-bag.model';
import { MapPoint } from '../../../../ui/models/map-point.model';
import { ChartCategoryPoint, SiloMonitoringKpis } from '../models/silo-monitoring-kpis.model';

/**
 * Funciones puras sobre `SiloBag[]`, sin dependencia de Angular. Reciben siempre el set YA
 * FILTRADO, así KPIs y gráficos siguen los filtros activos. Los pesos vienen en kg y salen en tn.
 */

const KG_PER_TON = 1000;
const AGE_BUCKETS: Array<{ key: string, label: string, from: number, to: number | null }> = [
  { key: '0-30', label: '0-30', from: 0, to: 30 },
  { key: '31-90', label: '31-90', from: 31, to: 90 },
  { key: '91-180', label: '91-180', from: 91, to: 180 },
  { key: '180+', label: '>180', from: 181, to: null }
];

function toTons(kg: number | null | undefined): number {
  return kg != null && Number.isFinite(kg) ? kg / KG_PER_TON : 0;
}

/** Parsea un valor de filtro (siempre string, viene de queryParams) a número finito, o null si no aplica. */
function toFilterNumber(raw: any): number | null {
  if (raw == null || raw === '') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function hasValidGeo(bag: SiloBag): boolean {
  return typeof bag.latitude === 'number' && typeof bag.longitude === 'number' &&
    Number.isFinite(bag.latitude) && Number.isFinite(bag.longitude) &&
    !(bag.latitude === 0 && bag.longitude === 0);
}

function ageBucketOf(days: number): { key: string, label: string } {
  const bucket = AGE_BUCKETS.find(b => days >= b.from && (b.to === null || days <= b.to)) || AGE_BUCKETS[AGE_BUCKETS.length - 1];
  return { key: bucket.key, label: bucket.label };
}

/** Filtra según los queryParams de la página: todos opcionales, combinados con AND. */
export function filterBags(bags: SiloBag[], filters: Record<string, any> = {}): SiloBag[] {
  const species = filters['species'];
  const harvest = filters['harvest'];
  const organization = filters['organization'];
  const countryside = filters['countryside'];
  const lot = filters['lot'];
  const province = filters['province'];
  const locality = filters['locality'];
  const status = filters['status'];
  const committed = filters['committed']; // 'available' | 'committed' | undefined (= todas)
  const alert = filters['alert']; // 'with' | 'without' | undefined
  const age = filters['age']; // '0-30' | '31-90' | '91-180' | '180+'
  const insured = filters['insured']; // 'with' | 'without'
  const vrr = filters['vrr']; // 'ok' | 'alert'
  const q = (filters['q'] || '').toString().trim().toLowerCase();
  const orderReference = (filters['order_reference'] || '').toString().trim().toLowerCase();
  const tonsFrom = toFilterNumber(filters['tons_from']);
  const tonsTo = toFilterNumber(filters['tons_to']);

  return bags.filter(bag => {
    if (species && bag.grain_type !== species) return false;
    if (harvest && bag.harvest !== harvest) return false;
    if (organization && bag.organization !== organization) return false;
    if (countryside && bag.countryside_name !== countryside) return false;
    if (lot && bag.lot_name !== lot) return false;
    if (province && bag.province !== province) return false;
    if (locality && bag.locality !== locality) return false;
    if (status && bag.status !== status) return false;
    if (committed === 'committed' && !bag.committed) return false;
    if (committed === 'available' && bag.committed) return false;
    if (orderReference) {
      const reference = (bag.commitment?.reference || '').toString().toLowerCase();
      if (!reference.includes(orderReference)) return false;
    }
    if (alert === 'with' && !bag.hasAlert) return false;
    if (alert === 'without' && bag.hasAlert) return false;
    if (tonsFrom != null && bag.bagged_weight < tonsFrom) return false;
    if (tonsTo != null && bag.bagged_weight > tonsTo) return false;
    if (age) {
      // Sin bagged_date no se puede determinar la antigüedad: se excluye de cualquier bucket.
      if (!bag.bagged_date) return false;
      if (ageBucketOf(bag.days_since_bagging).key !== age) return false;
    }
    if (insured === 'with' && !bag.insured) return false;
    if (insured === 'without' && bag.insured) return false;
    if (vrr === 'ok' && bag.vrr !== 'ok') return false;
    if (vrr === 'alert' && bag.vrr !== 'alert') return false;
    if (q) {
      const haystack = [bag.silobag_name, bag.lot_name, bag.silo_id].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

export function computeKpis(bags: SiloBag[]): SiloMonitoringKpis {
  const total = bags.length;

  let remainingStockKg = 0;
  let inputWeightKg = 0;
  let extractedKg = 0;
  let alertCount = 0;
  let alertStockKg = 0;
  let committedCount = 0;
  let committedStockKg = 0;
  let closedCount = 0;
  let humidityWeightedSum = 0;
  let humidityWeightTotal = 0;
  let ageSum = 0;
  let ageCount = 0;
  let maxAge = 0;
  let withoutGeo = 0;

  const fields = new Set<string>();
  const lots = new Set<string>();
  const species = new Set<string>();
  const harvests = new Set<string>();

  bags.forEach(bag => {
    const remaining = bag.remaining_stock ?? 0;
    remainingStockKg += remaining;
    inputWeightKg += bag.input_weight ?? 0;
    extractedKg += bag.totalExtracted ?? 0;

    if (bag.hasAlert) {
      alertCount++;
      alertStockKg += remaining;
    }
    if (bag.committed) {
      committedCount++;
      committedStockKg += remaining;
    }
    if (bag.active === false || bag.extractedAll === true) {
      closedCount++;
    }
    if (bag.humidity != null) {
      humidityWeightedSum += bag.humidity * (remaining || 1);
      humidityWeightTotal += (remaining || 1);
    }
    if (bag.bagged_date) {
      ageSum += bag.days_since_bagging;
      ageCount++;
      maxAge = Math.max(maxAge, bag.days_since_bagging);
    }
    if (!hasValidGeo(bag)) {
      withoutGeo++;
    }
    if (bag.countryside_name) fields.add(bag.countryside_name);
    if (bag.lot_name) lots.add(bag.lot_name);
    if (bag.grain_type) species.add(bag.grain_type);
    if (bag.harvest) harvests.add(bag.harvest);
  });

  const inputTons = toTons(inputWeightKg);
  const extractedTons = toTons(extractedKg);

  return {
    totalBags: total,
    remainingStockTons: toTons(remainingStockKg),
    inputWeightTons: inputTons,
    extractedTons,
    extractionPct: inputTons > 0 ? (extractedTons / inputTons) * 100 : 0,
    alertBagsCount: alertCount,
    alertPct: total > 0 ? (alertCount / total) * 100 : 0,
    alertStockTons: toTons(alertStockKg),
    committedCount,
    committedStockTons: toTons(committedStockKg),
    availableCount: total - committedCount,
    availableStockTons: toTons(remainingStockKg - committedStockKg),
    closedCount,
    avgHumidity: humidityWeightTotal > 0 ? humidityWeightedSum / humidityWeightTotal : null,
    avgAgeDays: ageCount > 0 ? ageSum / ageCount : null,
    maxAgeDays: ageCount > 0 ? maxAge : null,
    bagsWithoutGeo: withoutGeo,
    distinctFields: fields.size,
    distinctLots: lots.size,
    distinctSpecies: species.size,
    distinctHarvests: harvests.size
  };
}

function groupStockBy(bags: SiloBag[], keyFn: (bag: SiloBag) => string | null | undefined): ChartCategoryPoint[] {
  const totals = new Map<string, number>();

  bags.forEach(bag => {
    const key = keyFn(bag) || '—';
    totals.set(key, (totals.get(key) || 0) + toTons(bag.remaining_stock));
  });

  return Array.from(totals.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
}

export function stockBySpecies(bags: SiloBag[]): ChartCategoryPoint[] {
  return groupStockBy(bags, bag => bag.grain_type);
}

export function stockByHarvest(bags: SiloBag[]): ChartCategoryPoint[] {
  return groupStockBy(bags, bag => bag.harvest);
}

/** Top N campos por stock + agregado "Otros" para no saturar el gráfico. `othersLabel` viene traducido del componente. */
export function stockByField(bags: SiloBag[], topN = 8, othersLabel = 'Otros'): ChartCategoryPoint[] {
  const all = groupStockBy(bags, bag => bag.countryside_name);
  if (all.length <= topN) return all;

  const top = all.slice(0, topN);
  const othersTotal = all.slice(topN).reduce((sum, p) => sum + p.value, 0);
  return [...top, { label: othersLabel, value: othersTotal }];
}

export function ageDistribution(bags: SiloBag[]): { tons: ChartCategoryPoint[] } {
  const tons = new Map<string, number>();
  AGE_BUCKETS.forEach(b => tons.set(b.label, 0));

  bags.forEach(bag => {
    if (!bag.bagged_date) return;
    const bucket = ageBucketOf(bag.days_since_bagging);
    tons.set(bucket.label, (tons.get(bucket.label) || 0) + toTons(bag.remaining_stock));
  });

  return { tons: AGE_BUCKETS.map(b => ({ label: b.label, value: tons.get(b.label) || 0 })) };
}

export function committedVsAvailableBySpecies(bags: SiloBag[]): { species: string[], committed: number[], available: number[] } {
  const committedTotals = new Map<string, number>();
  const availableTotals = new Map<string, number>();

  bags.forEach(bag => {
    const key = bag.grain_type || '—';
    const totals = bag.committed ? committedTotals : availableTotals;
    totals.set(key, (totals.get(key) || 0) + toTons(bag.remaining_stock));
  });

  const speciesSet = Array.from(new Set([...committedTotals.keys(), ...availableTotals.keys()]));
  return {
    species: speciesSet,
    committed: speciesSet.map(species => committedTotals.get(species) || 0),
    available: speciesSet.map(species => availableTotals.get(species) || 0)
  };
}

export function stockByStatus(bags: SiloBag[]): { statuses: string[], normal: number[], withAlert: number[] } {
  const normalTotals = new Map<string, number>();
  const alertTotals = new Map<string, number>();

  bags.forEach(bag => {
    const key = bag.status || '—';
    const totals = bag.hasAlert ? alertTotals : normalTotals;
    totals.set(key, (totals.get(key) || 0) + toTons(bag.remaining_stock));
  });

  const statusSet = Array.from(new Set([...normalTotals.keys(), ...alertTotals.keys()]));
  return {
    statuses: statusSet,
    normal: statusSet.map(status => normalTotals.get(status) || 0),
    withAlert: statusSet.map(status => alertTotals.get(status) || 0)
  };
}

const SORT_ACCESSORS: Record<string, (bag: SiloBag) => string | number> = {
  silobag_name: bag => (bag.silobag_name || '').toLowerCase(),
  silo_id: bag => (bag.silo_id || '').toLowerCase(),
  grain_type: bag => (bag.grain_type || '').toLowerCase(),
  organization: bag => (bag.organization || '').toLowerCase(),
  countryside_name: bag => (bag.countryside_name || '').toLowerCase(),
  locality: bag => (bag.locality || '').toLowerCase(),
  province: bag => (bag.province || '').toLowerCase(),
  lot_name: bag => (bag.lot_name || '').toLowerCase(),
  harvest: bag => (bag.harvest || '').toLowerCase(),
  bagged_weight: bag => bag.bagged_weight || 0,
  humidity: bag => bag.humidity ?? -Infinity,
  bagged_date: bag => bag.bagged_date || '',
  days_since_bagging: bag => bag.days_since_bagging || 0,
  status: bag => (bag.status || '').toLowerCase()
};

/**
 * Ordena con el convenio `order_by` del proyecto (`campo` / `-campo`), sobre el set filtrado y
 * ANTES de paginar, para que el orden no quede acotado a la página visible.
 */
export function sortBags(bags: SiloBag[], orderBy: string | null | undefined): SiloBag[] {
  if (!orderBy) return bags;

  const desc = orderBy.startsWith('-');
  const field = desc ? orderBy.slice(1) : orderBy;
  const accessor = SORT_ACCESSORS[field];
  if (!accessor) return bags;

  return [...bags].sort((a, b) => {
    const va = accessor(a);
    const vb = accessor(b);
    // Orden natural para strings (así "Bs 7" queda antes que "Bs 35"), numérico para el resto.
    const cmp = typeof va === 'string' && typeof vb === 'string'
      ? va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' })
      : va < vb ? -1 : va > vb ? 1 : 0;
    return desc ? -cmp : cmp;
  });
}

/**
 * Convierte a puntos de mapa las bolsas con coordenadas válidas (el resto va al KPI
 * `bagsWithoutGeo`). Prioridad de color: con alarma > comprometida > disponible.
 */
export function toMapPoints(bags: SiloBag[]): MapPoint[] {
  return bags
    .filter(hasValidGeo)
    .map(bag => ({
      id: bag.silo_id,
      lat: bag.latitude as number,
      lng: bag.longitude as number,
      title: bag.silobag_name || bag.silo_id,
      variant: (bag.hasAlert ? 'alert' : bag.committed ? 'committed' : 'available') as MapPoint['variant'],
      hover: {
        code: bag.silo_id,
        species: bag.grain_type,
        baggedDate: bag.bagged_date ?? null,
        lot: bag.lot_name,
        stockKg: bag.remaining_stock ?? (bag.bagged_weight != null ? bag.bagged_weight * KG_PER_TON : null),
        harvest: bag.harvest
      }
    }));
}
